import React, { useEffect, useState } from 'react';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { Link, useLocation } from 'react-router-dom';

import ThirdPartyAuthButtons from 'components/ThirdPartyAuthButtons';
import { useAuthBrandingButtonClassName } from 'hooks/authBranding/useAuthBrandingButtonClassName';
import { useAuthInputBorderClassName } from 'hooks/authBranding/useAuthInputBorderClassName';
import { useInterval } from 'lib/reactHelpers/hooks';
import { getPathnameWithQueryParams, stringifyLocationQuery } from 'lib/url';
import auth from 'services/auth';
import {
    composeLoginFormShowDataLayerPayload,
    gTag,
} from 'services/google/tagManager';
import { t } from 'ttag';

import { Nullable } from '../../types/general';
import { LoginFormFields } from './types';

import HorizontalLineSeparator from '../ui/HorizontalLineSeparator';
import Button from '../ui/button/button';
import Checkbox from '../ui/checkbox/checkbox';
import Input from '../ui/input/input';
import Message from '../ui/message/message';
import Title from '../ui/title/title';

import PageTitle from '../pageTitle/pageTitle';
import { useRegistrationSource } from '../registration/hooks/useRegistrationSource';
import { ERROR_MESSAGE, HANDLED_ERRORS_BY_CODES } from './constants';
import { useLoginSuccessEffect } from './useLoginSuccessEffect';
import { loginFormResolver } from './validation';

import css from './loginForm.css';

/**
 *
 * @deprecated
 * @see `cs/components/loginForm/new/NewLoginForm.tsx`
 */
const LoginForm: React.FC = () => {
    const authBrandingButtonClassName = useAuthBrandingButtonClassName();
    const authInputBorderClassName = useAuthInputBorderClassName();
    const [formErrorMessage, setFormErrorMessage] = useState<string>('');
    const [requestLockTime, setRequestLockTime] = useState<Nullable<number>>(
        null,
    );
    const {
        control,
        handleSubmit,
        formState,
        watch: getActualFormValues,
    } = useForm<LoginFormFields>({
        resolver: loginFormResolver,
        defaultValues: {
            email: '',
            password: '',
            remember: true,
        },
    });
    const location = useLocation();
    const sourceLink = useRegistrationSource();
    const loginSuccessEffect = useLoginSuccessEffect();
    const isDisabledLoginButton = formState.isSubmitting || !!requestLockTime;
    const isRequestLockTimeMoreMinute =
        !!requestLockTime && requestLockTime > 60;
    const requestLockTimeText = isRequestLockTimeMoreMinute
        ? t`годину`
        : t`${requestLockTime} с.`;
    const isTTNAppContext = sourceLink === 'ttn';

    const onSubmit: SubmitHandler<LoginFormFields> = async ({
        email,
        password,
        remember,
    }) => {
        try {
            setFormErrorMessage('');
            const {
                next_url: nextUrl,
                is_2fa_enabled: is2faEnabled,
            } = await auth.login({
                email,
                password,
                remember,
            });
            loginSuccessEffect(nextUrl, is2faEnabled);
        } catch (error) {
            const errorMessage = HANDLED_ERRORS_BY_CODES.includes(error.code)
                ? error.reason
                : ERROR_MESSAGE;

            if (error.code === 'too_many_request' && error.details?.lock_time) {
                setRequestLockTime(error.details.lock_time);
                return;
            }

            setFormErrorMessage(errorMessage);
        }
    };

    useInterval(
        () => {
            if (!requestLockTime) {
                return;
            }
            setRequestLockTime(requestLockTime - 1);
        },
        requestLockTime ? 1000 : null,
    );

    useEffect(() => {
        gTag(composeLoginFormShowDataLayerPayload());
    }, []);

    return (
        <form className={css.root} onSubmit={handleSubmit(onSubmit)}>
            <PageTitle>{t`Вхід`}</PageTitle>
            <Title className={css.title}>{t`Вхід`}</Title>
            <div className={css.hint} data-qa="qa_user_registration">
                {t`У вас ще нема облікового запису?`}{' '}
                <Link
                    to={getPathnameWithQueryParams(
                        '/auth/registration/register-user',
                        location.search,
                    )}
                    className={css.link}
                >
                    {t`Зареєструйтесь`}
                </Link>
            </div>
            {config.GOOGLE_AUTH_ON && !isTTNAppContext && (
                <>
                    <ThirdPartyAuthButtons
                        microsoftAuthOn
                        googleAuthOn
                        appleAuthOn
                        onError={(error) => setFormErrorMessage(error.reason)}
                    />
                    <HorizontalLineSeparator
                        className={css.horizontalLineSeparator}
                    >{t`або`}</HorizontalLineSeparator>
                </>
            )}
            <div className={css.form} data-qa="qa_entrance_form">
                <div className={css.inputFields}>
                    <Controller
                        control={control}
                        name="email"
                        render={({ field, fieldState }) => (
                            <div className={css.inputWrapper}>
                                <label htmlFor={field.name}>{t`Email`}</label>
                                <Input
                                    type="text"
                                    autoFocus
                                    autoComplete="section-login login"
                                    value={field.value}
                                    error={!!fieldState.error?.message}
                                    errorMessage={fieldState.error?.message}
                                    placeholder={t`Наприклад, example@mail.com`}
                                    onChange={field.onChange}
                                    className={authInputBorderClassName}
                                />
                            </div>
                        )}
                    />
                    <Controller
                        control={control}
                        name="password"
                        render={({ field, fieldState }) => (
                            <div className={css.inputWrapper}>
                                <label htmlFor={field.name}>{t`Пароль`}</label>
                                <Input
                                    type="password"
                                    autoComplete="section-login password"
                                    value={field.value}
                                    error={!!fieldState.error?.message}
                                    errorMessage={fieldState.error?.message}
                                    onChange={field.onChange}
                                    className={authInputBorderClassName}
                                />
                            </div>
                        )}
                    />
                    {!!requestLockTime && (
                        <Message sizeSmall type="error">
                            {t`Ви використали усі спроби для двофакторної аутентифікації. Спробуйте через ${requestLockTimeText}`}
                        </Message>
                    )}
                    {formErrorMessage && (
                        <div>
                            <Message type="error" sizeSmall>
                                {formErrorMessage}
                            </Message>
                        </div>
                    )}
                </div>
                <div className={css.link} data-qa="qa_remind_pass">
                    <Link
                        to={{
                            pathname: '/auth/password/remind',
                            search: stringifyLocationQuery({
                                email: getActualFormValues('email'),
                            }),
                        }}
                        className={css.link}
                    >
                        {t`Забули пароль?`}
                    </Link>
                </div>
                <div className={css.checkboxWrapper}>
                    <Controller
                        control={control}
                        name="remember"
                        render={({ field }) => (
                            <Checkbox
                                checked={field.value}
                                onChange={field.onChange}
                                text={t`Запам’ятати мене`}
                                className={css.checkbox}
                            />
                        )}
                    />
                </div>
                <Button
                    type="submit"
                    width="full"
                    theme="cta"
                    dataQa="qa_entrance_btn"
                    isLoading={formState.isSubmitting}
                    disabled={isDisabledLoginButton}
                    className={authBrandingButtonClassName}
                >
                    {t`Увiйти`}
                </Button>
            </div>
        </form>
    );
};

export default LoginForm;
